import SessionLanguage from "../domain/value-objects/SessionLanguage";

export class Language {
  static readonly ARABIC = new Language("ar", "Arabic");
  static readonly ENGLISH = new Language("en", "English");
  static readonly FRENCH = new Language("fr", "French");
  static readonly GERMAN = new Language("de", "German");
  static readonly HEBREW = new Language("he", "Hebrew");
  static readonly HINDI = new Language("hi", "Hindi");
  static readonly ITALIAN = new Language("it", "Italian");
  static readonly PORTUGUESE = new Language("pt", "Portuguese");
  static readonly RUSSIAN = new Language("ru", "Russian");
  static readonly SPANISH = new Language("es", "Spanish");
  static readonly UKRAINIAN = new Language("uk", "Ukrainian");
  static readonly URDU = new Language("ur", "Urdu");
  static readonly POLISH = new Language("pl", "Polish");
  static readonly GREEK = new Language("el", "Greek");
  static readonly ROMANIAN = new Language("ro", "Romanian");
  static readonly SERBIAN = new Language("sr", "Serbian");
  static readonly INDONESIAN = new Language("id", "Indonesian");
  static readonly MANDARIN = new Language("cmn", "Mandarin");
  static readonly CANTONESE = new Language("yue", "Cantonese");
  static readonly HUNGARIAN = new Language("hu", "Hungarian");
  static readonly DUTCH = new Language("nl", "Dutch");
  static readonly CROATIAN = new Language("hr", "Croatian");
  static readonly BULGARIAN = new Language("bg", "Bulgarian");
  static readonly ESTONIAN = new Language("et", "Estonian");
  static readonly LATVIAN = new Language("lv", "Latvian");
  static readonly LITHUANIAN = new Language("lt", "Lithuanian");
  static readonly BOSNIAN = new Language("bs", "Bosnian");
  static readonly SLOVAKIAN = new Language("sk", "Slovakian");
  static readonly MONTENEGRIN = new Language("cnr", "Montenegrin");
  static readonly TURKISH = new Language("tr", "Turkish");
  static readonly ALBANIAN = new Language("sq", "Albanian");
  static readonly THAI = new Language("th", "Thai");
  static readonly MALAY = new Language("ms", "Malay");
  static readonly FILIPINO = new Language("fil", "Filipino");
  static readonly JAPANESE = new Language("ja", "Japanese");
  static readonly NORWEGIAN = new Language("no", "Norwegian");
  static readonly SWEDISH = new Language("sv", "Swedish");
  static readonly DANISH = new Language("da", "Danish");
  static readonly FINNISH = new Language("fi", "Finnish");

  private constructor(
    public readonly code: SessionLanguage,
    public readonly label: string,
  ) {}

  public static findLanguageByCode = (
    languageCode: string,
  ): Language | null => {
    return Object.values(Language).find(
      (it) => it.code?.toUpperCase() === languageCode?.toUpperCase(),
    );
  };

  public static findLanguageByLabel = (
    languageLabel: string,
  ): Language | null => {
    if (!languageLabel) {
      return null;
    }

    return Object.values(Language).find(
      (it) => it.label?.toUpperCase() === languageLabel.toUpperCase(),
    );
  };
}

export const bookableLanguages = [
  Language.ALBANIAN,
  Language.ARABIC,
  Language.BOSNIAN,
  Language.BULGARIAN,
  Language.CANTONESE,
  Language.CROATIAN,
  Language.DANISH,
  Language.DUTCH,
  Language.ENGLISH,
  Language.ESTONIAN,
  Language.FINNISH,
  Language.FILIPINO,
  Language.FRENCH,
  Language.GERMAN,
  Language.GREEK,
  Language.HEBREW,
  Language.HINDI,
  Language.HUNGARIAN,
  Language.INDONESIAN,
  Language.ITALIAN,
  Language.JAPANESE,
  Language.LATVIAN,
  Language.LITHUANIAN,
  Language.MALAY,
  Language.MANDARIN,
  Language.MONTENEGRIN,
  Language.NORWEGIAN,
  Language.POLISH,
  Language.PORTUGUESE,
  Language.ROMANIAN,
  Language.RUSSIAN,
  Language.SERBIAN,
  Language.SLOVAKIAN,
  Language.SPANISH,
  Language.SWEDISH,
  Language.THAI,
  Language.TURKISH,
  Language.UKRAINIAN,
  Language.URDU,
];

export const automatedSummarySupportedLanguages = [
  Language.DANISH,
  Language.DUTCH,
  Language.ENGLISH,
  Language.FINNISH,
  Language.FRENCH,
  Language.GERMAN,
  Language.HINDI,
  Language.INDONESIAN,
  Language.ITALIAN,
  Language.JAPANESE,
  Language.NORWEGIAN,
  Language.POLISH,
  Language.PORTUGUESE,
  Language.RUSSIAN,
  Language.SPANISH,
  Language.SWEDISH,
  Language.TURKISH,
  Language.UKRAINIAN,
];

export const DEFAULT_LANGUAGE = "en" as const;
export const HIGH_PRIORITY_LANGUAGES = ["en", "es", "de"] as const;
export const COUNTRY_LANGUAGE_MAP: Record<string, readonly string[]> = {
  // English-speaking countries (empty - will default to English via DEFAULT_LANGUAGE)
  US: [],
  UK: [],
  GB: [],
  AU: [],
  CA: ["fr"],
  IE: [],
  NZ: [],
  ZA: [],

  // Spanish-speaking countries
  ES: ["es"],
  MX: ["es"],
  AR: ["es"],
  CO: ["es"],
  CL: ["es"],
  PE: ["es"],
  VE: ["es"],

  // European countries
  FR: ["fr"],
  BE: ["fr", "nl", "de"],
  CH: ["de", "fr", "it"],
  DE: ["de"],
  AT: ["de"],
  IT: ["it"],
  PT: ["pt"],
  BR: ["pt"],
  NL: ["nl"],
  SE: ["sv"],
  NO: ["no"],
  DK: ["da"],
  FI: ["fi"],
  PL: ["pl"],
  RU: ["ru"],
  TR: ["tr"],
  GR: ["el"],

  // Middle East & Africa
  SA: ["ar"],
  AE: ["ar"],
  EG: ["ar"],
  MA: ["ar", "fr"],

  // Asia-Pacific
  CN: ["cmn"],
  HK: ["yue", "cmn"],
  JP: ["ja"],
  KR: ["ko"],
  IN: ["hi"],
  TH: ["th"],
  ID: ["id"],
  MY: ["ms"],
  PH: ["fil"],
} as const;
